import { formatDate } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { FormSection } from '@strata/react-form-section';
import { Hyperlink } from '@strata/react-hyperlink';
import { DetailValue } from '@strata/react-label-value-pairs';
import { StatusIcon } from '@strata/react-status-icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import {
    getHealthPlanNotesAttestationForReferral,
    HealthPlanNotesAttestation,
} from 'api/clientAPI';
import { HealthPlanNotesAgreeDialog } from 'components/shared/dialogs/HealthPlanNotesAgreeDialog';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';

interface HealthPlanNotesProps {
    payerId?: string;
    referralId?: string;
    attestationRow?: boolean;
}

export const HealthPlanNotes = ({
    payerId = undefined,
    referralId = undefined,
    attestationRow = false,
}: HealthPlanNotesProps) => {
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const [showHealthPlanNotesAgreeDialog, setShowHealthPlanNotesAgreeDialog] = useState(false);
    const [attestation, setAttestation] = useState<HealthPlanNotesAttestation>();

    useEffect(() => {
        (async () => {
            if (payerId && referralId) {
                try {
                    setAttestation(
                        await getHealthPlanNotesAttestationForReferral({
                            npi: selectedNpi!,
                            referralId,
                            payerId,
                        }),
                    );
                } catch (err) {
                    handleError('Error retrieving health plan notes attestation.', err as Error);
                    setAttestation(undefined);
                }
            }
        })();
    }, [selectedNpi, payerId, referralId]);

    return (
        <FormSection
            sectionTitle="Health Plan Notes"
            content={
                <div className="sds_content-container padding__top--medium padding__bottom--small">
                    <div className="padding__left--medium padding__right--medium">
                        <div className="margin__bottom--small">
                            <DetailValue value="Highmark Wholecare Network Essentials" />
                        </div>
                        <div className="sds_health-plan-notes__description margin__bottom--medium">
                            <Hyperlink
                                linkText="View provider requirements "
                                navUrl={AppRoutes.healthPlanNotes}
                            />
                            related to servicing a Specialty medication for a Highmark Wholecare
                            member.
                        </div>
                    </div>
                    {attestation && attestationRow && (
                        <>
                            <HealthPlanNotesAgreeDialog
                                isOpen={showHealthPlanNotesAgreeDialog}
                                onSecondaryButtonClick={() =>
                                    setShowHealthPlanNotesAgreeDialog(false)
                                }
                                onSubmitSuccess={() => {
                                    setAttestation({ attestation_date: dayjs().toISOString() });
                                    setShowHealthPlanNotesAgreeDialog(false);
                                }}
                                referralId={referralId}
                                payerId={payerId}
                            />
                            <div
                                style={{ display: 'flex', alignItems: 'center' }}
                                className="sds_content-container__bottom-row padding__top--small padding__left--medium padding__right--medium"
                            >
                                <div
                                    className="flex__gap--small padding__top--small padding__bottom--small"
                                    style={{ flex: 1, alignItems: 'center' }}
                                >
                                    {attestation?.attestation_date && (
                                        <StatusIcon status={StatusIcon.Statuses.GOOD} />
                                    )}
                                    <span>Attestation:</span>
                                    <span>
                                        {attestation?.attestation_date
                                            ? `Completed on ${formatDate(
                                                  attestation.attestation_date,
                                              )}`
                                            : '--'}
                                    </span>
                                </div>
                                {!attestation?.attestation_date && (
                                    <ActionButtonText
                                        label="Agree to Highmark Wholecare Network Essentials"
                                        onClick={() => setShowHealthPlanNotesAgreeDialog(true)}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            }
        />
    );
};
